import { invoiceCode, stockCode, voucherCode, type AccountRef, type Decimal, type GroupOption, type InvoiceKind, type Named, type StockKind, type VoucherKind } from "./api.js";
import { assetInvoiceCode } from "./assets.js";

// Financial reports of the correction pass of 2026-10-27 (docs/financial-reports.md): the cash flow statement and its
// activity mappings, the cash flow indicators, and the source document of a ledger line for drill-down.

/** The document that made a journal entry (or that the reversal reverses), for a link from a report row. */
export interface SourceRef {
  type: "invoice" | "voucher" | "stock" | "asset_invoice";
  kind: string;
  id: string;
  code: string | null;
  /** The row is the reversal of that document. */
  reversal: boolean;
}

/** The code of a source document ("SI-12", "CO-3", "TR-4", "AP-2"). */
export function sourceCode(type: SourceRef["type"], kind: string, number: string | null): string | null {
  if (!number) return null;
  switch (type) {
    case "invoice": return invoiceCode(kind as InvoiceKind, number);
    case "voucher": return voucherCode(kind as VoucherKind, number);
    case "stock": return stockCode(kind as StockKind, number);
    case "asset_invoice": return assetInvoiceCode(number);
  }
}

/** The screen of a source document. */
export function sourcePath(s: Pick<SourceRef, "type" | "kind" | "id">): string {
  switch (s.type) {
    case "invoice": case "stock": return `/invoices/show/${s.id}`;
    case "asset_invoice": return `/assets-purchase/show/${s.id}`;
    case "voucher": return s.kind === "cash_transfer" ? `/transfer/${s.id}` : s.kind === "opening_balance" ? `/opening-balance/${s.id}` : `/receipt/${s.id}`;
  }
}

export type CashActivity = "operating" | "investing" | "financing";
/**
 * Where a cash movement's counterpart is classified: an activity by its mapping, `other_cash` for cash accounts outside
 * the report's scope (another branch's or another user's treasuries: transfers, not activity), or `unmapped`.
 */
export type CashFlowGroup = CashActivity | "other_cash" | "unmapped";
export const CASH_FLOW_GROUPS: CashFlowGroup[] = ["operating", "investing", "financing", "other_cash", "unmapped"];

export interface CashFlowAccountRow {
  account: AccountRef; typeId: string;
  /** Which mapping classified it: the account's own, its group's or the nearest group above it (rule H-6), or none (unmapped and other cash). */
  mappedBy: "account" | "type" | null;
  inflow: Decimal; outflow: Decimal; net: Decimal;
}
export interface CashFlowSection { group: CashFlowGroup; inflow: Decimal; outflow: Decimal; net: Decimal; rows: CashFlowAccountRow[] }
export interface CashFlowDto {
  from: string | null; to: string | null; branch: Named | null;
  /** Cash and cash equivalents: the accounts of the treasuries in scope. */
  cash: (AccountRef & { treasuries: Named[] })[];
  opening: Decimal; inflow: Decimal; outflow: Decimal; net: Decimal; closing: Decimal;
  sections: CashFlowSection[];
  /** Entries whose counterparts fall in more than one activity with opposite signs (shown as they are; review them). */
  mixedEntries: number;
  /** The period's movement on the cash accounts in the ledger, and its difference from `net` (zero unless an unbalanced migrated entry moved cash). */
  ledgerNet: Decimal; difference: Decimal;
}
export interface CashFlowLineDto { entryId: string; number: string | null; entryAt: string; statement: string; source: SourceRef | null; account: AccountRef; group: CashFlowGroup; amount: Decimal }
export interface CashFlowLinesDto { rows: CashFlowLineDto[]; total: Decimal }

export interface CashFlowMappingsDto {
  /**
   * `activity`: the group's own mapping; `inherited`: the nearest group above that has one (applies when the group has
   * none of its own), with that group's id.
   */
  types: { type: GroupOption; activity: CashActivity | null; inherited: { activity: CashActivity; fromId: string } | null }[];
  accounts: { account: AccountRef & { typeId: string; level1Id: number }; activity: CashActivity | null; cash: boolean }[];
}
export interface CashFlowMappingsInput { types: { typeId: string; activity: CashActivity | null }[]; accounts: { accountId: string; activity: CashActivity | null }[] }

/** Proposed cash flow indicators (docs/financial-reports.md, "Indicators"): each with its inputs, never a guess. */
export type CashIndicatorKey =
  | "operating_cash_flow" | "free_cash_flow" | "net_cash_flow" | "cash_ratio" | "operating_cash_flow_ratio"
  | "cash_flow_margin" | "cash_to_profit" | "days_cash_on_hand";
export interface CashIndicator {
  key: CashIndicatorKey;
  unit: "amount" | "ratio" | "days";
  /** Null when the denominator is zero: the indicator is not defined for the period. */
  value: Decimal | null;
  numerator: Decimal;
  denominator: Decimal | null;
  state: "ok" | "zero_denominator";
}
export interface CashIndicatorsDto {
  from: string | null; to: string | null; branch: Named | null; days: number; indicators: CashIndicator[];
  /** Counterpart accounts of the period's cash movements with no activity mapping, and their net: left out of operating, investing and financing until classified (never guessed). */
  unmapped: { accounts: number; net: Decimal };
}

// ---------------------------------------------------------------- item movement (correction pass item 5)

/** What a stock movement is, from its document: invoices by kind, stock documents by kind. */
export type ItemMoveType = "opening" | "purchase" | "sale" | "sale_return" | "purchase_return" | "transfer" | "damaged" | "count" | "compromise";
export const ITEM_MOVE_TYPES: ItemMoveType[] = ["opening", "purchase", "sale", "sale_return", "purchase_return", "transfer", "damaged", "count", "compromise"];
export interface ItemMovementRow {
  seq: string; at: string; type: ItemMoveType;
  /** The document, with `reversal` when this row undoes it (a reversed invoice or stock document). */
  source: SourceRef;
  warehouse: Named; branch: Named;
  /** In the item's base unit. */
  qtyIn: Decimal; qtyOut: Decimal; balance: Decimal; value: Decimal;
}
export interface ItemMovementDto {
  item: { id: string; code: string; nameEn: string; nameAr: string };
  unit: Named;
  from: string | null; to: string | null; branch: Named | null; warehouse: Named | null;
  /** Every posted movement before `from` (drafts never move stock). */
  opening: Decimal;
  rows: ItemMovementRow[];
  totalIn: Decimal; totalOut: Decimal; closing: Decimal;
  /** In and out of the period by type. */
  byType: { type: ItemMoveType; qtyIn: Decimal; qtyOut: Decimal }[];
  /**
   * Reconciliation with stock on hand in the same warehouses: the sum of every movement ever (`ledger`) against the
   * quantity held now (`onHand`). `difference` is zero when they agree; the closing of a period that ends today equals both.
   */
  ledger: Decimal; onHand: Decimal; difference: Decimal;
}

// ---------------------------------------------------------------- product import (correction pass item 8)

/** The columns of the product import file, in the template's order. Codes and barcodes are text. */
export const ITEM_IMPORT_FIELDS = [
  "code", "nameAr", "nameEn", "category", "itemKind", "taxType", "baseUnit", "baseBarcode", "priceRetail", "priceWholesale", "priceHalf",
  "unit2", "unit2Pieces", "unit2Barcode", "unit2PriceRetail", "unit2PriceWholesale", "unit2PriceHalf",
  "minQty", "discount", "priceIncludeTax", "quickList", "descriptionAr", "descriptionEn"
] as const;
/**
 * The simplified sheet (request of 31 Oct 2026; docs/product-import.md): eight columns the user fills, in this order,
 * used by the template and by the export alike (the export adds the read-only cost after them). Codes and barcodes are text. The quantity and the tax are not columns: stock is
 * entered later on a stock document, and the tax treatment of the prices is chosen once for the whole file.
 */
export const ITEM_SHEET_FIELDS = ["code", "nameAr", "nameEn", "category", "categoryEn", "itemClass", "priceRetail", "baseBarcode"] as const;
/**
 * Columns an export shows for reading only, after the template's (review of 31 Oct 2026, item 1): the cost price. Not
 * in the template, never imported, and it does not change any item's cost (cost comes from purchases and stock).
 */
export const ITEM_SHEET_READONLY = ["costPrice"] as const;
export const ITEM_SHEET_REQUIRED = ["nameAr", "category", "itemClass", "priceRetail"] as const;
/**
 * Technical columns the export adds, hidden in the sheet: the item's and the category's ids, and whether the item's
 * price includes tax. They let a re-import find the same item and category and keep each item's own price treatment.
 */
export const ITEM_SHEET_HIDDEN = ["itemId", "categoryRef", "priceIncludeTax"] as const;
/** The values of the item type column, matched in English or Arabic. */
export const ITEM_CLASS_VALUES = { stock: ["مخزني", "stock", "inventory", "inventory item", "stock item"], nonStock: ["غير مخزني", "non-stock", "non stock", "nonstock", "non-inventory", "non-inventory item", "service"] } as const;
export type ItemImportField = (typeof ITEM_IMPORT_FIELDS)[number] | (typeof ITEM_SHEET_FIELDS)[number] | (typeof ITEM_SHEET_READONLY)[number] | (typeof ITEM_SHEET_HIDDEN)[number];
export const ITEM_IMPORT_ALL_FIELDS: ItemImportField[] = [...new Set<ItemImportField>([...ITEM_IMPORT_FIELDS, ...ITEM_SHEET_FIELDS, ...ITEM_SHEET_READONLY, ...ITEM_SHEET_HIDDEN])];
export const ITEM_IMPORT_REQUIRED: ItemImportField[] = ["code", "nameAr", "nameEn", "category", "itemKind", "taxType", "baseUnit", "priceRetail"];
export const ITEM_IMPORT_MAX_ROWS = 5000;
/** One row of the file, as mapped to the import's fields; `row` is its line in the file (header = 1). */
export type ItemImportRow = { row: number } & Partial<Record<ItemImportField, string>>;
export interface ItemImportInput {
  /** Made once per confirmed import and sent again on a retry: the import happens once. */
  requestId: string;
  fileName: string;
  /** SHA-256 of the file, hex. */
  fileDigest: string;
  /** true: check every row and report, creating nothing (the preview). */
  dryRun: boolean;
  rows: ItemImportRow[];
  /**
   * Request of 29 Oct 2026, item 2. Defaults the user chose from the business's own lists (ids), used only in rows
   * whose cell is empty; nothing is guessed and nothing is created.
   */
  defaults?: { category?: string | null; itemKind?: string | null; taxType?: string | null; baseUnit?: string | null };
  /**
   * Rows whose code is an existing item's: "refuse" (the default) reports them; "update" changes that item's names,
   * category, kind, tax, prices and the other filled cells (needs itemUpdate). Units, conversions and barcodes are
   * never changed by an import: a row that would change them is refused.
   */
  existing?: "refuse" | "update";
  /** A row with one of the two names: "refuse" (the default), or "copy" the other name, as written (never translated). */
  missingName?: "refuse" | "copy";
  /**
   * Request of 31 Oct 2026: "simple" is the simplified sheet (ITEM_SHEET_FIELDS; an export adds the read-only cost). Its rules: the Arabic name is
   * required and an empty English name takes the Arabic one; an empty code is numbered; items are found by id, then
   * code, then barcode, never by name; the item type picks the kind chosen for it (`kinds`); the tax type and base unit
   * of new items come from `defaults`; the cost column is not imported; `mode` and `priceMode` apply; and the confirmed
   * import is all or nothing: one row with a problem and nothing is written. Absent: "full", as before.
   */
  format?: "full" | "simple";
  /** "create": new items only (existing ones are skipped, unchanged); "upsert": update and add; "update": update only. */
  mode?: "create" | "upsert" | "update";
  /** Whether the file's selling prices include VAT, for every row without its own (hidden) value. */
  priceMode?: "inclusive" | "exclusive";
  /** The item kind for each item type (ids from the business's kinds that hold stock, or not). */
  kinds?: { stock?: string | null; nonStock?: string | null };
}
/** GET /items/sheet-options: what the simplified template, the export and the import screen start from. */
export interface ItemSheetOptions {
  /** Whether most of the business's items have prices including tax (no items: false). The import screen's default. */
  pricesIncludeTax: boolean;
  /** The code the next numbered item would take (shown as an example; the import numbers its rows itself). */
  nextCode: string;
}
/** GET /items/export: every item as a row of the simplified sheet, with the hidden columns. */
export interface ItemExportRow {
  itemId: string; code: string; nameAr: string; nameEn: string;
  categoryRef: string; category: string; categoryEn: string;
  /** "stock" or "nonStock" (from the item kind); null when the item has no kind. */
  itemClass: "stock" | "nonStock" | null;
  priceRetail: Decimal; priceIncludeTax: boolean;
  /** The weighted average cost over the caller's warehouses, as on the item page; "" when nothing is in stock. */
  costPrice: Decimal | "";
  /** The base unit's barcodes, joined by " | ". */
  baseBarcode: string;
}
export interface ItemExportDto { rows: ItemExportRow[]; pricesIncludeTax: boolean; at: string }
export interface ItemImportError { row: number; field: ItemImportField | null; code: string; value?: string }
/** What each row would do (preview) or did (import). */
export interface ItemImportRowResult {
  row: number; code: string;
  /** "skip": an existing item in the "create" mode, left as it is. */
  action: "create" | "update" | "unchanged" | "skip" | "error";
  changes?: ItemImportField[];
  /** The existing item the row was matched to, and how (simple format). */
  match?: { id: string; by: "id" | "code" | "barcode" };
  /** The code a new item will get when its cell was empty (simple format). */
  newCode?: string;
}
export interface ItemImportResult {
  dryRun: boolean;
  /** This request was already imported: the answer is that import's, and nothing was created again. */
  replayed: boolean;
  total: number; valid: number; created: number;
  /** Existing items changed (existing: "update"); 0 otherwise. */
  updated: number;
  errors: ItemImportError[];
  createdItems: { row: number; id: string; code: string }[];
  updatedItems: { row: number; id: string; code: string }[];
  rows: ItemImportRowResult[];
  /** An earlier import of the same file (same digest), if any. */
  previous: { at: string; by: string; created: number } | null;
  /** Things the user should know that are not problems, e.g. "cost_ignored" (simple format). */
  notices?: string[];
}
